import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from './services/firebase';
import AuthProvider, { useAuth } from './context/AuthContext'; 
import DemoProvider, { useDemo } from './context/DemoContext'; 
import { TenantProvider } from './context/TenantContext';
import { useTheme } from './context/ThemeProvider'; 

import ApexGenesisForm from './features/admin/ApexGenesisForm';
import Login from './features/auth/Login';
import Gatekeeper from './components/Gatekeeper';
import DailyStrikeZone from './features/crm/DailyStrikeZone';
import PipelineBoard from './features/crm/PipelineBoard';
import EekoGateway from './features/onboarding/EekoGateway';
import TelemetryCenter from './features/telemetry/TelemetryCenter'; 
import CustomerProfile from './features/crm/CustomerProfile';
import NetworkLedger from './features/crm/NetworkLedger';
import ContractorLedger from './features/crm/ContractorLedger';
import ContractorProfile from './features/crm/ContractorProfile';
import GenesisAccountCreator from './features/crm/GenesisAccountCreator';
import CommissionDashboard from './features/crm/CommissionDashboard';
import ResourceVault from './features/crm/ResourceVault';
import MigrationUploader from './features/admin/MigrationUploader';
import W2Ledger from './features/crm/W2Ledger';
import PayrollCenter from './features/admin/PayrollCenter';
import HRDocsVault from './features/admin/HRDocsVault';

// New Enterprise Settings & Integration Imports
import Settings from './features/crm/Settings';
import GoogleCallback from './pages/GoogleCallback';
import GlobalCalendar from './features/calendar/GlobalCalendar'; // NEW: Global Calendar Import

// Master Control Plane Import
import FoundersControlPlane from './features/founder/FoundersControlPlane';

// FTL Enterprise Onboarding Import
import FTLOnboarding from './features/onboarding/FTLOnboarding';

function DemoBanner() {
  const { isDemoMode } = useDemo();
  if (!isDemoMode) return null;
  return (
    <div className="fixed top-0 left-0 w-full h-7 bg-amber-500 text-slate-900 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 z-[9999] shadow-md shadow-amber-500/20">
      <i className="fa-solid fa-triangle-exclamation"></i> Sandbox Training Mode Active — Safe to Explore
    </div>
  );
}

function Sidebar() {
  // Destructure the strict PBAC/RBAC roles from AuthContext
  const { currentUser, isFounder, isClientOwner, isAdmin, loading } = useAuth();
  const { isDemoMode, setIsDemoMode } = useDemo(); 
  const { isDarkMode, toggleTheme } = useTheme(); 
  const navigate = useNavigate();
  const location = useLocation();
  
  // Create an impenetrable administrative boundary 
  const hasAdminClearance = isFounder || isClientOwner || isAdmin;

  const [viewMode, setViewMode] = useState(hasAdminClearance ? 'founder' : 'operator');

  useEffect(() => {
    // If a standard operator somehow gets viewMode set to founder, instantly revert it
    if (!loading && !hasAdminClearance && viewMode === 'founder') {
      setViewMode('operator');
    }
  }, [hasAdminClearance, loading, viewMode]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const navItemClass = (path) => `
    flex items-center gap-3 w-full px-6 py-3 transition-all duration-200 text-[11px] font-bold uppercase tracking-widest border-l-2
    ${location.pathname === path 
      ? 'bg-slate-700/60 text-white border-emerald-500 shadow-[inset_0px_1px_0px_0px_rgba(255,255,255,0.05)]' 
      : 'text-slate-400 hover:bg-slate-700/30 hover:text-slate-200 border-transparent'}
  `;

  const currentDropdownValue = isDemoMode ? 'demo' : viewMode;

  const handleWorkspaceChange = (e) => {
    const val = e.target.value;
    if (val === 'demo') {
      setIsDemoMode(true);
      setViewMode('operator'); 
    } else {
      setIsDemoMode(false);
      setViewMode(val); 
    }
  };

  if (loading || !currentUser) return null;

  return (
    <div className={`w-64 bg-slate-800 border-r border-slate-700 flex flex-col h-screen fixed left-0 transition-all duration-300 ${isDemoMode ? 'top-7' : 'top-0'}`}>
      
      <div className="p-6 border-b border-slate-700/80 bg-slate-900 flex flex-col items-start">
        <div className="flex items-center gap-3 w-full">
          <div className="flex-shrink-0 w-8 h-8">
            <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 2L2 9L16 16L30 9L16 2Z" fill="#10B981" fillOpacity="0.1" stroke="#10B981" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M2 23L16 30L30 23V9L16 16L2 9V23Z" fill="#0F172A" />
              <path d="M2 23L16 30L30 23" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 30V16" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="16" cy="16" r="2.5" fill="#22D3EE" className="animate-pulse" />
            </svg>
          </div>
          <div className="flex flex-col flex-1 overflow-hidden">
            <h1 className="text-2xl tracking-tight flex items-baseline">
              <span className="font-black text-white">eeko</span>
              <span className="font-light text-slate-300">System</span>
            </h1>
          </div>
        </div>
        <div className="mt-3 text-[0.55rem] font-bold tracking-[0.25em] text-slate-400 uppercase whitespace-nowrap">
          AN APE<span className="text-cyan-400">X</span> RETAIL-SUITE TOOL
        </div>

        {/* SECURE WORKSPACE TOGGLE */}
        {hasAdminClearance ? (
          <div className="mt-6 w-full">
            <label className="text-[8px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 block">
              Active Workspace Mode
            </label>
            <div className="relative">
              <select 
                value={currentDropdownValue}
                onChange={handleWorkspaceChange}
                className={`w-full bg-slate-800 border text-[10px] font-bold py-2.5 px-3 uppercase tracking-widest focus:outline-none appearance-none cursor-pointer shadow-inner transition-colors ${isDemoMode ? 'border-amber-500 text-amber-500' : 'border-slate-600 text-slate-200 focus:border-emerald-500'}`}
              >
                <option value="founder">Founder Data Center</option>
                <option value="operator">Sales Floor (Live)</option>
                <option value="demo">Training Simulator (Demo)</option>
              </select>
              <i className={`fa-solid fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-[10px] pointer-events-none ${isDemoMode ? 'text-amber-500' : 'text-slate-400'}`}></i>
            </div>
          </div>
        ) : (
          <div className="mt-6 w-full">
            <label className="text-[8px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 block">
              Clearance Level
            </label>
            <div className="w-full bg-slate-800 border border-slate-700 text-[10px] font-bold py-2.5 px-3 uppercase tracking-widest text-emerald-500 shadow-inner flex items-center">
              <i className="fa-solid fa-lock text-slate-500 mr-2"></i> Tactical Floor
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 py-6 overflow-y-auto custom-scrollbar">
        {/* SECURE FOUNDER/ADMIN BLOCK */}
        {hasAdminClearance && viewMode === 'founder' && !isDemoMode && (
          <>
            <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 px-6">Workspace</div>
            <Link to="/" className={navItemClass('/')}><i className="fa-solid fa-bolt w-4 text-center opacity-80"></i> Strike Zone</Link>
            <Link to="/pipeline" className={navItemClass('/pipeline')}><i className="fa-solid fa-bars-staggered w-4 text-center opacity-80"></i> Pipeline</Link>
            <Link to="/calendar" className={navItemClass('/calendar')}><i className="fa-solid fa-calendar-days w-4 text-center opacity-80"></i> Global Calendar</Link>
            <Link to="/ledger" className={navItemClass('/ledger')}><i className="fa-solid fa-network-wired w-4 text-center opacity-80"></i> Network Ledger</Link>
            <Link to="/contractors" className={navItemClass('/contractors')}><i className="fa-solid fa-users-gear w-4 text-center opacity-80"></i> 1099 Contractors</Link>
            <Link to="/w2-workforce" className={navItemClass('/w2-workforce')}><i className="fa-solid fa-id-badge w-4 text-center opacity-80"></i> W-2 Workforce</Link>
            <Link to="/commissions" className={navItemClass('/commissions')}><i className="fa-solid fa-vault w-4 text-center opacity-80"></i> Commission Engine</Link>
            <Link to="/resources" className={navItemClass('/resources')}><i className="fa-solid fa-folder-open w-4 text-center opacity-80"></i> Resource Vault</Link>
            
            {/* EXTERNAL APEX OS LINK - FOUNDER */}
            <a href="https://os.apexretailsuite.com" target="_blank" rel="noopener noreferrer" className={navItemClass('external')}>
              <i className="fa-solid fa-rocket w-4 text-center opacity-80"></i> Deploy Apex OS
            </a>
            
            {isFounder && (
              <>
                <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-8 mb-2 px-6">Command Center</div>
                <Link to="/founder-control" className={navItemClass('/founder-control')}><i className="fa-solid fa-microchip w-4 text-center opacity-80"></i> Master Control Plane</Link>
                <Link to="/telemetry" className={navItemClass('/telemetry')}><i className="fa-solid fa-satellite-dish w-4 text-center opacity-80"></i> Telemetry</Link>
                <Link to="/payroll" className={navItemClass('/payroll')}><i className="fa-solid fa-money-check-dollar w-4 text-center opacity-80"></i> Payroll & HR</Link>
                <Link to="/hr-vault" className={navItemClass('/hr-vault')}><i className="fa-solid fa-folder-closed w-4 text-center opacity-80"></i> HR & Docs Vault</Link>
                <Link to="/genesis" className={navItemClass('/genesis')}><i className="fa-solid fa-server w-4 text-center opacity-80"></i> Genesis Gateway</Link>
                <Link to="/migration" className={navItemClass('/migration')}><i className="fa-solid fa-cloud-arrow-up w-4 text-center opacity-80"></i> Data Migration</Link>
              </>
            )}
          </>
        )}

        {/* TACTICAL OPERATOR BLOCK */}
        {(viewMode === 'operator' || isDemoMode) && (
          <div className="animate-in fade-in slide-in-from-left-2 duration-300">
            <div className={`text-[10px] font-black uppercase tracking-widest mb-2 px-6 ${isDemoMode ? 'text-amber-500' : 'text-emerald-500'}`}>
              {isDemoMode ? 'Sandbox Floor' : 'Tactical Floor'}
            </div>
            <Link to="/operator" className={navItemClass('/operator')}><i className="fa-solid fa-bolt w-4 text-center opacity-80"></i> Strike Zone</Link>
            <Link to="/operator/pipeline" className={navItemClass('/operator/pipeline')}><i className="fa-solid fa-filter-circle-dollar w-4 text-center opacity-80"></i> My Sales Funnel</Link>
            <Link to="/operator/calendar" className={navItemClass('/operator/calendar')}><i className="fa-solid fa-calendar-days w-4 text-center opacity-80"></i> Store Schedule</Link>
            <Link to="/operator/ledger" className={navItemClass('/operator/ledger')}><i className="fa-solid fa-address-book w-4 text-center opacity-80"></i> My Active Clients</Link>
            <Link to="/operator/intake" className={navItemClass('/operator/intake')}><i className="fa-solid fa-file-signature w-4 text-center opacity-80"></i> Submit Deal</Link>
            <Link to="/operator/commissions" className={navItemClass('/operator/commissions')}><i className="fa-solid fa-money-bill-trend-up w-4 text-center opacity-80"></i> Commission Tracker</Link>
            
            <div className={`text-[10px] font-black uppercase tracking-widest mt-8 mb-2 px-6 ${isDemoMode ? 'text-amber-500' : 'text-emerald-500'}`}>Assets</div>
            <Link to="/operator/resources" className={navItemClass('/operator/resources')}><i className="fa-solid fa-folder-open w-4 text-center opacity-80"></i> Resource Vault</Link>
            
            {/* EXTERNAL APEX OS LINK - OPERATOR */}
            <a href="https://os.apexretailsuite.com" target="_blank" rel="noopener noreferrer" className={navItemClass('external')}>
              <i className="fa-solid fa-rocket w-4 text-center opacity-80"></i> Deploy Apex OS
            </a>
          </div>
        )}
      </div>

      <div className="p-4 border-t border-slate-700/80 bg-slate-900">
        <div className="flex items-center gap-3 mb-4 px-2">
          <div className="w-8 h-8 rounded-none bg-slate-800 border border-slate-600 flex items-center justify-center text-slate-300 font-bold text-xs uppercase shadow-sm shrink-0">
            {currentUser.email.charAt(0)}
          </div>
          <div className="overflow-hidden">
            <p className="text-xs font-bold text-slate-200 truncate">{currentUser.email}</p>
            <p className={`text-[9px] font-bold uppercase tracking-widest mt-0.5 flex items-center gap-1 ${isDemoMode ? 'text-amber-500' : 'text-emerald-500'}`}>
              <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${isDemoMode ? 'bg-amber-500' : 'bg-emerald-500'}`}></span> {isDemoMode ? 'Sandbox' : 'Active Session'}
            </p>
          </div>
        </div>

        {/* SYSTEM SETTINGS */}
        <Link 
          to="/settings"
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-2 border border-slate-700 bg-slate-800 hover:border-slate-500 hover:text-white rounded-none text-[10px] font-bold text-slate-400 uppercase tracking-widest transition-colors"
        >
          <i className="fa-solid fa-sliders"></i> System Settings
        </Link>

        {/* HIGH VISIBILITY THEME TOGGLE */}
        <button
          onClick={toggleTheme}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-2 border border-slate-700 bg-slate-800 hover:border-slate-500 hover:text-white rounded-none text-[10px] font-bold text-slate-400 uppercase tracking-widest transition-colors"
        >
          {isDarkMode ? <><i className="fa-solid fa-sun text-amber-400"></i> Light Mode</> : <><i className="fa-solid fa-moon text-indigo-400"></i> Dark Mode</>}
        </button>

        <button 
          onClick={handleLogout} 
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-700 bg-slate-800 hover:border-slate-500 hover:text-white rounded-none text-[10px] font-bold text-slate-400 uppercase tracking-widest transition-colors"
        >
          <i className="fa-solid fa-power-off"></i> Sign Out
        </button>
      </div>
    </div>
  );
}

function AppLayout() {
  const { isDemoMode } = useDemo();
  const location = useLocation();
  const navigate = useNavigate();

  const isPublicRoute = location.pathname === '/login';

  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
      </Routes>
    );
  }

  return (
    <>
      <DemoBanner />
      <div className={`flex h-screen bg-slate-50 dark:bg-slate-900 font-sans overflow-hidden transition-all duration-300 ${isDemoMode ? 'mt-7' : 'mt-0'}`}>
        <Sidebar />
        <main className="flex-1 ml-64 p-8 h-full overflow-y-auto pb-24 relative">
          <div className="max-w-6xl mx-auto h-full">
            <Routes>
              {/* FTL ROUTE: Takes over the viewport when triggered */}
              <Route path="/ftl" element={<Gatekeeper><FTLOnboarding onComplete={() => navigate('/')} /></Gatekeeper>} />

              <Route path="/gateway" element={<EekoGateway />} />
              <Route path="/" element={<Gatekeeper><DailyStrikeZone /></Gatekeeper>} />
              <Route path="/pipeline" element={<Gatekeeper><PipelineBoard /></Gatekeeper>} />
              
              {/* FOUNDER RESTRICTED ROUTES */}
              <Route path="/founder-control" element={<Gatekeeper requireFounder={true}><FoundersControlPlane /></Gatekeeper>} />
              <Route path="/telemetry" element={<Gatekeeper requireFounder={true}><TelemetryCenter /></Gatekeeper>} />
              <Route path="/payroll" element={<Gatekeeper requireFounder={true}><PayrollCenter /></Gatekeeper>} />
              <Route path="/hr-vault" element={<Gatekeeper requireFounder={true}><HRDocsVault /></Gatekeeper>} />
              <Route path="/genesis" element={<Gatekeeper requireFounder={true}><ApexGenesisForm /></Gatekeeper>} />
              <Route path="/migration" element={<Gatekeeper requireFounder={true}><MigrationUploader /></Gatekeeper>} />
              
              <Route path="/customer/:id" element={<Gatekeeper><CustomerProfile /></Gatekeeper>} />
              <Route path="/ledger" element={<Gatekeeper><NetworkLedger /></Gatekeeper>} />
              <Route path="/contractors" element={<Gatekeeper><ContractorLedger /></Gatekeeper>} />
              <Route path="/contractor/:id" element={<Gatekeeper><ContractorProfile /></Gatekeeper>} />
              <Route path="/commissions" element={<Gatekeeper><CommissionDashboard /></Gatekeeper>} />
              <Route path="/resources" element={<Gatekeeper><ResourceVault /></Gatekeeper>} />
              <Route path="/w2-workforce" element={<Gatekeeper><W2Ledger /></Gatekeeper>} />
              <Route path="/calendar" element={<Gatekeeper><GlobalCalendar /></Gatekeeper>} />

              <Route path="/operator" element={<Gatekeeper><DailyStrikeZone isOperatorView={true} /></Gatekeeper>} />
              <Route path="/operator/pipeline" element={<Gatekeeper><PipelineBoard isOperatorView={true} /></Gatekeeper>} />
              <Route path="/operator/ledger" element={<Gatekeeper><NetworkLedger isOperatorView={true} /></Gatekeeper>} />
              <Route path="/operator/calendar" element={<Gatekeeper><GlobalCalendar /></Gatekeeper>} />
              
              {/* THE DEAL DESK ROUTE FOR JONATHAN */}
              <Route path="/operator/intake" element={<Gatekeeper><GenesisAccountCreator isOperatorView={true} /></Gatekeeper>} />
              
              <Route path="/operator/commissions" element={<Gatekeeper><CommissionDashboard isOperatorView={true} /></Gatekeeper>} />
              <Route path="/operator/resources" element={<Gatekeeper><ResourceVault isOperatorView={true} /></Gatekeeper>} />
              
              {/* NEW SETTINGS & INTEGRATION ROUTES */}
              <Route path="/settings" element={<Gatekeeper><Settings /></Gatekeeper>} />
              <Route path="/integrations/google/callback" element={<Gatekeeper><GoogleCallback /></Gatekeeper>} />
            </Routes>
          </div>
        </main>
      </div>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <TenantProvider>
        <DemoProvider>
          <Router>
            <AppLayout />
          </Router>
        </DemoProvider>
      </TenantProvider>
    </AuthProvider>
  );
}

export default App;